// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BankUploadDrawer } from '@client/banks/layouts/drawer/bank-upload-drawer';
import { BankUploadProvider } from '@client/banks/providers/bank-upload-provider';
import { Badge, Button, Tooltip } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { StepsProvider } from '@shared/ui-kit/hooks/use-steps';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { ReactNode } from 'react';
import { BankUploadStepsOptions } from './bank-upload-steps-options';
import { useBankUploadButton } from './use-bank-upload-button';
import { type DrawerTriggerProps } from '@shared/ui-kit/hooks/use-drawer';

export type BankUploadButtonProps = {
  externalConnectContent?: ReactNode;
};

export const BankUploadButton = defineComponent({
  displayName: 'BankUploadButton',
  useSetup: useBankUploadButton,
  Slots: {
    Trigger: ({
      hasDraft,
      ...props
    }: DrawerTriggerProps & {
      hasDraft: boolean;
    }) => (
      <Tooltip
        title={
          <Translation
            i18nKey="aggregator.create-bank.draft-tooltip"
            ns="aggregator"
          />
        }
        disableHoverListener={!hasDraft}
      >
        <Badge color="warning" variant="dot" invisible={!hasDraft}>
          <Button
            {...props}
            type="button"
            variant="contained"
            color="secondary"
            startIcon={<AppIcon icon="Link" size="lg" color="light" />}
            data-testid="aggregator-connection-button"
          >
            <Translation i18nKey="aggregator.connect-bank" ns="aggregator" />
          </Button>
        </Badge>
      </Tooltip>
    ),
  },
  View: ({ hasDraft, externalConnectContent, Slots }) => (
    <StepsProvider {...BankUploadStepsOptions}>
      <BankUploadProvider externalConnectContent={externalConnectContent}>
        <BankUploadDrawer
          Trigger={props => <Slots.Trigger {...props} hasDraft={hasDraft} />}
        />
      </BankUploadProvider>
    </StepsProvider>
  ),
});
